import clsx from 'clsx';
import {
  OpsInfrastructureLevel,
  OpsNodeStatus,
} from '../../../../../libs/api-client/src/generated/models';
import { useLocale } from '../../i18n/LocaleProvider';
import type { OpsNode } from '../../lib/infrastructure-api';
import {
  formatBitRate,
  formatBytes,
  formatDuration,
  formatNumber,
  formatPercent,
  nodeStatusLabelKey,
  nodeStatusTone,
  shortLabel,
  worstLevel,
} from '../../lib/infrastructure';
import { Pill } from '../Pill';
import { RingGauge, Sparkline, Stat } from './InfrastructureParts';

/**
 * One server, in the queue card's shape: name and status pill on top, the border as the only
 * per-state signal on the card itself (amber/red when a figure is elevated or the node stopped
 * reporting), the figures below. Three gauges (CPU, RAM, disk), the numbers an operator reaches for
 * next (load, steal, uptime, pools, public traffic) and the last hour of CPU and RAM.
 */
export function NodeCard({
  node,
  nodePrefix,
}: {
  readonly node: OpsNode;
  /** The shared node-name prefix; the heading shows the rest, the full name is its tooltip. */
  readonly nodePrefix: string;
}) {
  const { t, locale } = useLocale();
  const worst = worstLevel([
    node.cpuLevel,
    node.memoryLevel,
    node.diskLevel,
    node.loadLevel,
  ]);
  const border =
    node.status === OpsNodeStatus.Stale ||
    worst === OpsInfrastructureLevel.Critical
      ? 'border-red-200'
      : worst === OpsInfrastructureLevel.Warn
        ? 'border-amber-200'
        : 'border-gray-200';

  return (
    <article
      className={clsx('surface flex min-w-0 flex-col gap-4 p-5', border)}
    >
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3
            className="truncate text-sm font-semibold text-gray-900"
            title={node.name}
          >
            {shortLabel(node.name, nodePrefix)}
          </h3>
          <p className="mt-0.5 truncate text-xs text-gray-500">
            {[
              t('infrastructure.node.role', { role: node.role }),
              node.serverType,
              node.location,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>
        <Pill tone={nodeStatusTone(node.status)}>
          {t(nodeStatusLabelKey(node.status))}
        </Pill>
      </header>

      {node.status === OpsNodeStatus.NoAgent ? (
        <p className="text-sm text-gray-600">
          {t('infrastructure.node.noAgent')}
        </p>
      ) : (
        <>
          {node.status === OpsNodeStatus.Stale && (
            <p className="text-xs font-medium text-red-600">
              {t('infrastructure.node.lastSeen', {
                age: formatDuration(node.ageSeconds),
              })}
            </p>
          )}
          <div
            className={clsx(
              'grid grid-cols-3 gap-2',
              node.status === OpsNodeStatus.Stale && 'opacity-50',
            )}
          >
            <RingGauge
              label={t('infrastructure.node.cpu')}
              percent={node.cpuPercent}
              display={formatPercent(node.cpuPercent, locale)}
              level={node.cpuLevel}
            />
            <RingGauge
              label={t('infrastructure.node.memory')}
              percent={node.memoryPercent}
              display={formatPercent(node.memoryPercent, locale)}
              level={node.memoryLevel}
              detail={t('infrastructure.node.of', {
                used: formatBytes(node.memoryUsedBytes, locale),
                total: formatBytes(node.memoryTotalBytes, locale),
              })}
            />
            <RingGauge
              label={t('infrastructure.node.disk')}
              percent={node.diskPercent}
              display={formatPercent(node.diskPercent, locale)}
              level={node.diskLevel}
              detail={t('infrastructure.node.of', {
                used: formatBytes(node.diskUsedBytes, locale),
                total: formatBytes(node.diskTotalBytes, locale),
              })}
            />
          </div>

          <dl className="grid grid-cols-2 gap-x-4 gap-y-3 border-t border-gray-100 pt-4">
            <Stat
              label={t('infrastructure.node.load')}
              value={t('infrastructure.node.loadValue', {
                one: formatNumber(node.load1, locale, 2),
                five: formatNumber(node.load5, locale, 2),
                fifteen: formatNumber(node.load15, locale, 2),
              })}
              sub={`${formatNumber(node.cpus, locale)} ${t('infrastructure.node.cpus')}`}
            />
            <Stat
              label={t('infrastructure.node.steal')}
              value={formatPercent(node.stealPercent, locale)}
            />
            <Stat
              label={t('infrastructure.node.uptime')}
              value={formatDuration(node.uptimeSeconds)}
            />
            <Stat
              label={t('infrastructure.node.pools')}
              value={
                node.pools.length === 0
                  ? '—'
                  : node.pools
                      .map(
                        (pool) =>
                          `${pool.name} ${formatNumber(pool.cap, locale)}${pool.auto ? ` (${t('infrastructure.node.auto')})` : ''}`,
                      )
                      .join(', ')
              }
            />
            {(node.networkInBps !== null || node.diskReadIops !== null) && (
              <>
                <Stat
                  label={t('infrastructure.node.network')}
                  value={`↓ ${formatBitRate(node.networkInBps, locale)}`}
                  sub={`↑ ${formatBitRate(node.networkOutBps, locale)}`}
                />
                <Stat
                  label={t('infrastructure.node.diskIo')}
                  value={t('infrastructure.node.diskIoValue', {
                    read: formatNumber(node.diskReadIops, locale, 1),
                    write: formatNumber(node.diskWriteIops, locale, 1),
                  })}
                />
              </>
            )}
          </dl>

          <div className="grid grid-cols-2 gap-3">
            <Sparkline
              title={t('infrastructure.node.cpuHistory')}
              points={node.history.map((p) => ({
                t: p.t,
                value: p.cpuPercent,
              }))}
              format={(v) => formatPercent(v, locale)}
              max={100}
            />
            <Sparkline
              title={t('infrastructure.node.memoryHistory')}
              points={node.history.map((p) => ({
                t: p.t,
                value: p.memoryPercent,
              }))}
              format={(v) => formatPercent(v, locale)}
              max={100}
            />
          </div>
        </>
      )}

      {node.status === OpsNodeStatus.NoAgent &&
        (node.networkInBps !== null || node.diskReadIops !== null) && (
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3 border-t border-gray-100 pt-4">
            <Stat
              label={t('infrastructure.node.network')}
              value={`↓ ${formatBitRate(node.networkInBps, locale)}`}
              sub={`↑ ${formatBitRate(node.networkOutBps, locale)}`}
            />
            <Stat
              label={t('infrastructure.node.diskIo')}
              value={t('infrastructure.node.diskIoValue', {
                read: formatNumber(node.diskReadIops, locale, 1),
                write: formatNumber(node.diskWriteIops, locale, 1),
              })}
            />
          </dl>
        )}
    </article>
  );
}
